import moment from 'moment';
import {
	compareStrings,
	isHierarchyNTEBased,
	nullSafeGet,
	nullSafeGetOrElse,
	nullSafeGetWithZero,
} from './DataAccessUtils';
import { ROLE_TYPES } from './DataConstants';
import { isAllowedToPerform, isSupplierAllowedToAccess, PERMISSION_NAMES } from './AuthUtils';
import { infinityButNumber } from '../constants/general';
import { ENTITY_REQUEST_ENTITY_TYPES } from '../types/entity_add_requests';

export const isInApprovalHierarchy = (currentUser, approvalHierarchy) => {
	const tiers = nullSafeGetOrElse('tierDetails', approvalHierarchy, []);
	const approvers = tiers.flatMap((tier) => tier.approvers);
	return approvers.some((approver) => approver.email === currentUser.email);
};

export const isUserAllowedToFullyApprove = (user, approvalHierarchy, companyConfig, amount = 0) => {
	if (approvalHierarchy) {
		const tiers = nullSafeGetOrElse('tierDetails', approvalHierarchy, []);
		const userTier = tiers.find((tier) =>
			tier.approvers.some((approver) => compareStrings(approver.email, user.email))
		);
		if (parseFloat(nullSafeGetOrElse('amountUpto', userTier, infinityButNumber)) >= amount) {
			return true;
		}
	} else {
		return user.userType === ROLE_TYPES.SUPPLIER
			? isSupplierAllowedToAccess(PERMISSION_NAMES.CAN_APPROVE_INVOICES, companyConfig, user.roles)
			: isAllowedToPerform(PERMISSION_NAMES.CAN_APPROVE_INVOICES, companyConfig, user.roles);
	}
	return false;
};

// Only approvers with an email count, matching the backend's `hasApprovers`/`firstUnapprovedTierWithApprovers`.
const tierHasApprovers = (tier) =>
	nullSafeGetOrElse('approvers', tier, []).some((approver) => nullSafeGet('email', approver));

// `nullSafeGetWithZero`, not `nullSafeGet`: a zero-limit tier must sort first, as on the backend,
// not fall through to unlimited.
const tierSortAmount = (tier) => {
	const amount = parseFloat(nullSafeGetWithZero('amountUpto', tier));
	return Number.isNaN(amount) ? Number.MAX_VALUE : amount;
};

const tierSortLabel = (tier) => {
	const label = parseInt(nullSafeGetWithZero('label', tier), 10);
	return Number.isNaN(label) ? Number.MAX_SAFE_INTEGER : label;
};

/**
 * Approval order: `amountUpto` ascending, then numeric `label`, with a missing amount (unlimited)
 * or label sorting last. Mirrors `ApprovalEnforcementUtil.sortTiers` on the backend — which tier
 * counts as "ahead" of the approver has to agree on both sides, or sequential enforcement does too.
 */
export const compareApprovalTiers = (a, b) =>
	tierSortAmount(a) - tierSortAmount(b) || tierSortLabel(a) - tierSortLabel(b);

const tierIsUnapproved = (tier) => nullSafeGetOrElse('approvedByUsers', tier, []).length === 0;

export const getFirstUnapprovedTierWithApprovers = (tiers) => {
	for (const tier of nullSafeGetOrElse('length', tiers, 0) ? tiers : []) {
		if (!tierHasApprovers(tier)) {
			continue;
		}
		if (tierIsUnapproved(tier)) {
			return tier;
		}
	}
	return undefined;
};

// Most recent of the given date-ish values, ignoring anything missing or unparseable.
export const latestDateTime = (values) => {
	const moments = (values || [])
		.filter((value) => value)
		.map((value) => moment(value))
		.filter((parsed) => parsed.isValid());
	return moments.length > 0
		? moments.reduce((latest, parsed) => (parsed.isAfter(latest) ? parsed : latest))
		: undefined;
};

/**
 * When the escalation clock starts running for a hierarchy: the most recent approval signature
 * anywhere in it, falling back to `pendingSince` — when the entity itself started waiting for
 * approval (invoice `publishedAt`/`disputedAt`, quote `submittedAt`, work order `createdAt`).
 * Mirrors `ApprovalEnforcementUtil.escalationUnblocks` on the backend.
 */
export const getEscalationClockStart = (tiers, pendingSince) => {
	const signedAtTimes = (nullSafeGetOrElse('length', tiers, 0) ? tiers : []).reduce(
		(times, tier) =>
			times.concat(
				nullSafeGetOrElse('approvedByUsers', tier, []).map((user) => nullSafeGet('at', user))
			),
		[]
	);
	return latestDateTime([...signedAtTimes, pendingSince]);
};

const indexOfTier = (tiers, targetTier) => {
	const identityIndex = tiers.indexOf(targetTier);
	if (identityIndex > -1) {
		return identityIndex;
	}
	return tiers.findIndex((tier) =>
		compareStrings(nullSafeGetOrElse('label', tier, ''), nullSafeGetOrElse('label', targetTier, ''))
	);
};

/**
 * Whether escalation lets `myTier` act out of sequence (OPE-5137). A tier configured with
 * `daysAfterEscalation` escalates to the tier behind it once that many days pass without an
 * approval — the backend notifies the later tier, so sequential enforcement has to let that tier
 * actually approve. Every tier standing in front of `myTier` that still hasn't approved must have
 * its own escalation window, and all of them must have elapsed; a tier with no window configured
 * keeps blocking forever, and with no clock to measure from escalation never fires.
 *
 * `tiers` must already be in approval order (amountUpto ascending, then label) — the order the
 * display components sort into and the order the backend's `sortTiers` produces. Every window is
 * measured from the same clock start rather than chained tier by tier, which is the leniency the
 * backend already settled on — the two gates have to agree or an allowed click turns into a 409.
 * FE mirror of `ApprovalEnforcementUtil.escalationUnblocks` (openwrench-app#2404).
 */
export const escalationUnblocksSequentialApproval = (tiers, myTier, pendingSince, now?) => {
	const sortedTiers = nullSafeGetOrElse('length', tiers, 0) ? tiers : [];
	const myTierIndex = indexOfTier(sortedTiers, myTier);
	if (myTierIndex < 0) {
		return false;
	}
	const blockingTiers = sortedTiers
		.slice(0, myTierIndex)
		.filter((tier) => tierHasApprovers(tier) && tierIsUnapproved(tier));
	if (blockingTiers.length === 0) {
		return false;
	}
	const clockStart = getEscalationClockStart(sortedTiers, pendingSince);
	if (!clockStart) {
		return false;
	}
	const asOf = now ? moment(now) : moment();
	return blockingTiers.every((tier) => {
		// Only a positive window unblocks, matching the backend's `days > 0`. A tier with no
		// escalation (or a 0-day one) keeps blocking. `nullSafeGet` would swallow a legitimate 0
		// here, so read it with the zero-preserving variant.
		const days = parseInt(nullSafeGetWithZero('daysAfterEscalation', tier), 10);
		return !Number.isNaN(days) && days > 0 && !asOf.isBefore(clockStart.clone().add(days, 'days'));
	});
};

/**
 * Whether sequential approval enforcement holds `myTier` back. `pendingSince` is when the entity
 * started waiting for approval and is only used for the escalation window — omit it and the clock
 * falls back to the latest approval signature on the hierarchy.
 */
export const checkSequentialApprovalBlock = (tiers, myTier, pendingSince?) => {
	const nextTier = getFirstUnapprovedTierWithApprovers(tiers);
	if (!nextTier) {
		return { blocked: false };
	}
	if (
		!compareStrings(
			nullSafeGetOrElse('label', nextTier, ''),
			nullSafeGetOrElse('label', myTier, '')
		)
	) {
		if (escalationUnblocksSequentialApproval(tiers, myTier, pendingSince)) {
			return { blocked: false, escalated: true };
		}
		return {
			blocked: true,
			reason: `Sequential approval is enforced. Tier ${nullSafeGetOrElse(
				'label',
				nextTier,
				'1'
			)} must approve first.`,
		};
	}
	return { blocked: false };
};

export const defaultTier = {
	name: '',
	approvers: [],
	showSearch: false,
	label: 1,
	id: 1,
};

export const getApprovalHierarchiesIndexLink = (userType) =>
	`/${userType}/approvalHierarchies/overview/templates`;

export const getApprovalHierarchyDetailLink = (userType, approvalHierarchyId) =>
	`/${userType}/approvalHierarchies/detail/templates/${approvalHierarchyId}/details`;

// Name a copy defaults to, before the user renames it in the copy dialog.
export const getApprovalHierarchyCopyName = (approvalHierarchy) =>
	`Copy of ${nullSafeGetOrElse('name', approvalHierarchy, 'Approval Hierarchy')}`;

/**
 * Strips the server-owned identity off a hierarchy so it can be POSTed back as a brand new one
 * under `name`. The backend derives `buyerCompanyId`/`createdBy` from the authenticated contact and
 * only reads `id`, `name` and `tierDetails` off the payload — a lingering `id` would turn the
 * create into an insert with an explicit primary key, so it has to go. Tier ids are dropped for the
 * same reason the create wizard drops them: they are re-derived from the tier's position.
 */
export const getApprovalHierarchyCopy = (approvalHierarchy, name?) => ({
	...approvalHierarchy,
	id: undefined,
	name: name || getApprovalHierarchyCopyName(approvalHierarchy),
	createdBy: undefined,
	createdByContact: undefined,
	createdAt: undefined,
	updatedAt: undefined,
	tierDetails: nullSafeGetOrElse('tierDetails', approvalHierarchy, []).map((tier) => ({
		...tier,
		id: undefined,
		showSearch: false,
		// The wizard stringifies labels on the way out; the copy has to match or the backend
		// rejects the tier (`label` is a string field).
		label: nullSafeGetOrElse('label', tier, 0).toString(),
	})),
});

export const approvalRuleTypes = {
	QUOTE: 'quote',
	INVOICE: 'invoice',
	PURCHASE_REQUEST: 'purchaseRequest',
	WORK_ORDER: 'workOrder',
	REQUEST_FOR_PROPOSAL: 'requestForProposal',
	ENTITY_REQUEST: 'entityRequest',
};

const ENTITY_REQUEST_TYPE_DISPLAY_NAMES = {
	[ENTITY_REQUEST_ENTITY_TYPES.supplier]: 'Supplier',
	[ENTITY_REQUEST_ENTITY_TYPES.asset]: 'Assets',
	[ENTITY_REQUEST_ENTITY_TYPES.part]: 'Parts',
};

export const getEntityRequestTypeDisplayName = (entityType) =>
	ENTITY_REQUEST_TYPE_DISPLAY_NAMES[entityType] || entityType;

/**
 * Entity types a Suggested Entity approval rule can be scoped to, mirroring the tabs on the
 * Suggested Entities page: parts are a supplier-side concept only, and assets and parts are both
 * disabled until the backend can materialize those entity types.
 */
export const getEntityRequestApprovalRuleOptions = (userType) => [
	{
		value: ENTITY_REQUEST_ENTITY_TYPES.supplier,
		label: getEntityRequestTypeDisplayName(ENTITY_REQUEST_ENTITY_TYPES.supplier),
		disabled: false,
	},
	{
		value: ENTITY_REQUEST_ENTITY_TYPES.asset,
		label: getEntityRequestTypeDisplayName(ENTITY_REQUEST_ENTITY_TYPES.asset),
		disabled: true,
	},
	...(userType === ROLE_TYPES.SUPPLIER
		? [
				{
					value: ENTITY_REQUEST_ENTITY_TYPES.part,
					label: getEntityRequestTypeDisplayName(ENTITY_REQUEST_ENTITY_TYPES.part),
					disabled: true,
				},
		  ]
		: []),
];

/**
 * Shared approval-hierarchy gating helpers.
 *
 * These were originally inline in ApprovalHierarchyDisplayQuote and are extracted here so
 * other entities (e.g. RFP bidder award/decline) can gate actions with identical semantics.
 * The quote component still carries its own inline copy for now; this is the single source
 * of truth for new consumers.
 */

// Highest `amountUpto` among tiers that already have any approver or decliner. 0 if none.
export const highestApprovalTierAmount = (tiers) => {
	const highestTiers = [...tiers]
		.filter((tier) => {
			const approvedByUsers = nullSafeGet('approvedByUsers', tier);
			const declinedByUsers = nullSafeGet('declinedByUsers', tier);
			if (approvedByUsers && declinedByUsers) {
				return approvedByUsers.concat(declinedByUsers).length > 0;
			}
			return false;
		})
		.map((tier) => nullSafeGetOrElse('amountUpto', tier, infinityButNumber));
	if (highestTiers.length > 0) {
		return highestTiers[highestTiers.length - 1];
	}
	return 0;
};

// FE replica of the backend `checkIfFullyApprovedUtil` (ApprovalHierarchyUtil.scala).
// Returns whether the hierarchy is fully approved for `total`: the highest tier with approvals must
// cover the amount, and every OTHER tier at that same amountUpto (different label) that has
// approvers must also have approved. Pass tiers that already include the current user's pending
// approval to determine whether they would be the final approver.
export const checkIfWillFullyApprovedUtil = (total, tiers = []) => {
	const amount = parseFloat(total) || 0;
	const amountOf = (tier) => parseFloat(nullSafeGetOrElse('amountUpto', tier, infinityButNumber));
	const labelOf = (tier) => {
		const parsed = parseInt(nullSafeGet('label', tier), 10);
		return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
	};

	const tiersSortedByAmountDescending = [...tiers].sort((a, b) => amountOf(b) - amountOf(a));
	const highestTierThatApproved = tiersSortedByAmountDescending.find(
		(tier) => nullSafeGetOrElse('approvedByUsers', tier, []).length > 0 && amountOf(tier) >= amount
	);
	if (!highestTierThatApproved) {
		return false;
	}

	const otherTiersWithSameAmountButDifferentLabel = tiersSortedByAmountDescending.filter((tier) => {
		const tierHasApprovers = nullSafeGetOrElse('approvers', tier, []).some((approver) =>
			nullSafeGet('email', approver)
		);
		const tierWithSameAmount = amountOf(tier) === amountOf(highestTierThatApproved);
		const tierWithDifferentLabel = labelOf(tier) !== labelOf(highestTierThatApproved);
		return tierHasApprovers && tierWithSameAmount && tierWithDifferentLabel;
	});

	return otherTiersWithSameAmountButDifferentLabel.every(
		(tier) => nullSafeGetOrElse('approvedByUsers', tier, []).length > 0 && amountOf(tier) >= amount
	);
};

// The tier the current user approves at. With a hierarchy, the tier whose approvers include
// the user (by email); without one, the first tier.
export const getUserApprovalTier = (tiers, currentUser, hasApprovalHierarchy) => {
	if (!hasApprovalHierarchy) {
		return tiers[0];
	}
	return tiers.find((tier) =>
		nullSafeGetOrElse('approvers', tier, []).some((approver) =>
			compareStrings(approver.email, currentUser.email)
		)
	);
};

/**
 * Sorts and annotates tiers for display, mirroring ApprovalHierarchyDisplayQuote.
 * `amount` is the entity total (exchange-rate-adjusted) used for NTE visibility.
 * Returns the annotated tiers (with `isVisible`, `message`, `lastAmount`) plus the
 * highest already-approved tier amount.
 */
export const buildApprovalTierView = (rawTiers, { amount = 0, exchangeRate = 1, currency }) => {
	const tiers = nullSafeGetOrElse('length', rawTiers, 0) ? rawTiers : [];
	const isNTEBased = isHierarchyNTEBased(tiers);
	let lessThanQuote = true;
	let highestValidAmount = 0;
	let highestApprovedAmount = 0;
	let lastAmount = 0;

	const annotated = [...tiers]
		.sort(compareApprovalTiers)
		.map((tier) => {
			if (nullSafeGetOrElse('approvedByUsers', tier, []).length > 0) {
				highestApprovedAmount = parseInt(nullSafeGet('amountUpto', tier)) || Infinity;
			}
			return {
				...tier,
				approvedByUsers: nullSafeGetOrElse('approvedByUsers', tier, []).filter(
					(user, index, users) =>
						index === users.findIndex((other) => compareStrings(other.email, user.email))
				),
				declinedByUsers: nullSafeGetOrElse('declinedByUsers', tier, []).filter(
					(user, index, users) =>
						index === users.findIndex((other) => compareStrings(other.email, user.email))
				),
			};
		})
		.map((tier, index, sortedTiers) => {
			let message;
			const isVisible = () => {
				if (!isNTEBased) {
					return true;
				}
				const tierAmount = parseInt(nullSafeGet('amountUpto', tier));
				if (
					highestApprovedAmount > tierAmount &&
					tier.approvedByUsers.length === 0 &&
					tier.declinedByUsers.length === 0
				) {
					message = 'A higher tier has already approved';
					return false;
				}
				if (
					highestApprovedAmount === tierAmount &&
					tier.approvers.length === 0 &&
					tier.approvedByUsers.length === 0
				) {
					message = 'An equal tier has approved and this tier has no approvers for this location';
					return false;
				}
				if (!lessThanQuote) {
					const previousAmount = parseInt(
						nullSafeGetOrElse('amountUpto', sortedTiers[index - 1], 0)
					);
					if (tier.approvedByUsers.length > 0 || tier.declinedByUsers.length > 0) {
						return true;
					}
					if (highestValidAmount === tierAmount && highestValidAmount === previousAmount) {
						return true;
					}
					message =
						currency && currency.format
							? `Approval from this tier isn't required for amounts less than ${currency.format(
									(previousAmount ? previousAmount : tierAmount) * exchangeRate
							  )}`
							: `Approval from this tier isn't required for this amount`;
					return false;
				}
				if (tierAmount && amount) {
					if (parseFloat(tierAmount) > parseFloat(amount)) {
						highestValidAmount = tierAmount;
						if (nullSafeGetOrElse('approvers.length', tier, 0) > 0) {
							lessThanQuote = false;
						}
					}
				} else {
					lessThanQuote = false;
				}
				return true;
			};
			lastAmount =
				nullSafeGetOrElse('amountUpto', sortedTiers[index - 1], 0) ===
				nullSafeGet('amountUpto', tier)
					? lastAmount
					: nullSafeGetOrElse('amountUpto', sortedTiers[index - 1], 0);
			return { ...tier, isVisible: isVisible(), message, lastAmount };
		});

	return { tiers: annotated, highestApprovedAmount, isNTEBased };
};

/**
 * Whether the current user may take an approval action (award/decline) at this amount,
 * mirroring ApprovalHierarchyDisplayQuote's hierarchy branch. Only meaningful when a
 * hierarchy is present; callers without a hierarchy should keep their existing behavior.
 * Returns { allowed, reason } — reason is shown in a Popover when disabled.
 */
export const getApprovalActionGate = ({
	hasApprovalHierarchy,
	myTier,
	requiredAmount,
	currentUser,
	sequentialApprovalEnforcement,
	tiers,
	defaultActionsAllowed = false,
	pendingSince = undefined,
}) => {
	const myTierAmount = nullSafeGet('amountUpto', myTier);
	if (hasApprovalHierarchy && myTier !== undefined) {
		if (sequentialApprovalEnforcement) {
			const seqCheck = checkSequentialApprovalBlock(tiers, myTier, pendingSince);
			if (seqCheck.blocked) {
				return { allowed: false, reason: seqCheck.reason };
			}
		}
		// The approver's tier must cover the amount being approved (unlimited tier = no amountUpto).
		if (myTierAmount === undefined || parseFloat(myTierAmount) >= requiredAmount) {
			const alreadyApproved = nullSafeGetOrElse('approvedByUsers', myTier, []).some((user) =>
				compareStrings(currentUser.email, user.email)
			);
			return alreadyApproved
				? { allowed: false, reason: 'Your tier has already approved.' }
				: { allowed: true, reason: undefined };
		}
		return { allowed: false, reason: 'This amount exceeds your approval limit.' };
	} else if (!hasApprovalHierarchy) {
		return {
			allowed: defaultActionsAllowed,
			reason: defaultActionsAllowed ? undefined : 'You are not allowed to perform this action.',
		};
	}
	return { allowed: false, reason: 'You are not in the approval hierarchy.' };
};
